/**
 * Due Diligence Module Presets
 * 
 * Central export for all checklist module definitions.
 * Each module contains categories with checklist item templates.
 */

export * from './modules/land-basics';
export * from './modules/soils';
export * from './modules/water';
export * from './modules/septic';
export * from './modules/utilities';
export * from './modules/access';
export * from './modules/legal';
export * from './modules/residential-structure';
export * from './modules/outbuildings';
export * from './modules/fencing';
export * from './modules/timber';
export * from './modules/agricultural';
export * from './modules/water-features';
export * from './modules/wildlife';
export * from './modules/environmental';
export * from './modules/flood-climate';
export * from './modules/proximity';
export * from './modules/financials';
export * from './modules/multiple-parcels';
export * from './modules/neighbors';
export * from './modules/property-visit';

import { ChecklistModuleDefinition, ModuleCode, ChecklistItemTemplate } from '../types/due-diligence';

import { landBasicsModule } from './modules/land-basics';
import { soilsModule } from './modules/soils';
import { waterModule } from './modules/water';
import { septicModule } from './modules/septic';
import { utilitiesModule } from './modules/utilities';
import { accessModule } from './modules/access';
import { legalModule } from './modules/legal';
import { residentialStructureModule } from './modules/residential-structure';
import { outbuildingsModule } from './modules/outbuildings';
import { fencingModule } from './modules/fencing';
import { timberModule } from './modules/timber';
import { agriculturalModule } from './modules/agricultural';
import { waterFeaturesModule } from './modules/water-features';
import { wildlifeModule } from './modules/wildlife';
import { environmentalModule } from './modules/environmental';
import { floodClimateModule } from './modules/flood-climate';
import { proximityModule } from './modules/proximity';
import { financialsModule } from './modules/financials';
import { multipleParcelsModule } from './modules/multiple-parcels';
import { neighborsModule } from './modules/neighbors';
import { propertyVisitModule } from './modules/property-visit';

/**
 * All module definitions indexed by code
 */
export const ALL_MODULES: Record<ModuleCode, ChecklistModuleDefinition> = {
  land_basics: landBasicsModule,
  soils: soilsModule,
  water: waterModule,
  septic: septicModule,
  utilities: utilitiesModule,
  access: accessModule,
  legal: legalModule,
  residential_structure: residentialStructureModule,
  outbuildings: outbuildingsModule,
  fencing: fencingModule,
  timber: timberModule,
  agricultural: agriculturalModule,
  water_features: waterFeaturesModule,
  wildlife: wildlifeModule,
  environmental: environmentalModule,
  flood_climate: floodClimateModule,
  proximity: proximityModule,
  financials: financialsModule,
  multiple_parcels: multipleParcelsModule,
  neighbors: neighborsModule,
  property_visit: propertyVisitModule,
};

/**
 * Get module definition by code
 */
export function getModuleDefinition(code: ModuleCode): ChecklistModuleDefinition {
  return ALL_MODULES[code];
}

/**
 * Get all items from selected modules
 */
export function getChecklistItems(moduleCodes: ModuleCode[]): ChecklistModuleDefinition[] {
  return moduleCodes.map(code => ALL_MODULES[code]);
}

/**
 * Count total items across modules
 */
export function countTotalItems(moduleCodes: ModuleCode[]): number {
  return moduleCodes.reduce((total, code) => {
    const module = ALL_MODULES[code];
    return total + module.categories.reduce((catTotal, cat) => catTotal + cat.items.length, 0);
  }, 0);
}

/**
 * Get item template by module and item code
 * Returns the template definition which includes dataFields and suggestedFlags
 */
export function getItemTemplate(moduleCode: string, itemCode: string): ChecklistItemTemplate | null {
  const module = ALL_MODULES[moduleCode as ModuleCode];
  if (!module) return null;
  
  for (const category of module.categories) {
    const item = category.items.find(i => i.code === itemCode);
    if (item) return item;
  }
  
  return null;
}

